import React, { useEffect, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getApiErrorMessage } from '../Services/Formaters';
import { handleApiError } from '../Services/FailApi';
import { MIN_PASSWORD_LENGTH_LABEL, PASSWORD_RULES } from '../lib/Password';
import { UserRoleSchema } from '../definitions/schemas';
import FormValidatorHelper from '../Services/FormValidatorService';
import SnackbarStore from '../stores/observables/SnackbarStore';
import UserInterface from '../graphql/User/UserInterface';
import useQuery from '../lib/hook/router/useQuery';
import userPendingActionsStoreInst, {
  PendingActions,
} from '../stores/observables/UserPendingActionsStore';
import UserStore, { UserRolesStore } from '../stores/observables/UserStore';

const ChangePasswordDialog: React.FC = () => {
  const location = useLocation();
  const { updateQuery } = useQuery();

  const [closeable, setCloseable] = useState(false);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userReady, setUserReady] = useState(false);

  const pendingSubscription = useRef<Subscription | null>(null);
  const userSubscription = useRef<Subscription | null>(null);

  const { current: validator } = useRef(new FormValidatorHelper());

  useEffect(() => {
    subscribeUserPendingActionsStore();
    subscribeUserRolesStore();

    return () => {
      pendingSubscription.current?.unsubscribe();
      userSubscription.current?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkDialogQuery(location.search);
  }, [location.search]);

  const checkDialogQuery = (search: string) => {
    const queryParams = new URLSearchParams(search);
    if (queryParams.get('dialog') === 'changePassword') {
      setCloseable(true);
      setOpen(true);
    }
  };

  const submitPasswordChange = () => {
    if (saving) return;
    setSubmitted(true);
    if (validator.isValid()) {
      setSaving(true);
      new UserInterface()
        .changePassword(password)
        .then((response: any) => {
          // Handle recovered 500 errors
          if (response.errors) {
            handleApiError(response.errors);
            setServerError(true);
            setSaving(false);
            return;
          }
          // Handle cacthed errors
          if (response.data.changePassword.errors.length > 0) {
            SnackbarStore.show(
              getApiErrorMessage(response.data.changePassword.errors)
            );
            setServerError(true);
            setSaving(false);
            return;
          }
          // Success! Close dialog and reload to restart the session
          setCloseable(false);
          setSaving(false);
          setOpen(false);
          setServerError(false);
          updateQuery({}, true);
          window.location.reload();
        })
        .catch((error: any) => {
          setServerError(true);
          setSaving(false);
          handleApiError(error);
        });
    }
  };

  const subscribeUserPendingActionsStore = () => {
    pendingSubscription.current = userPendingActionsStoreInst
      .getObservable()
      .subscribe((pending: PendingActions) => {
        if (pending.changePassword) {
          setCloseable(false);
          setOpen(true);
        }
      });
  };

  const subscribeUserRolesStore = () => {
    userSubscription.current = UserRolesStore.getObservable().subscribe(
      (userRoles: UserRoleSchema[]) => {
        setUserReady(userRoles.length > 0);
      }
    );
  };

  const onClose = () => {
    setCloseable(false);
    setOpen(false);
    updateQuery({});
  };

  validator.check('password', password, PASSWORD_RULES);

  return (
    <Dialog
      disableEscapeKeyDown
      maxWidth="sm"
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      open={open && userReady}
    >
      <DialogContent>
        <DialogContentText>
          Please set your password for Enable Us
        </DialogContentText>
        <DialogContentText noWrap>
          Username: <Box component="strong">{UserStore.get()?.email}</Box>
        </DialogContentText>
        <Box mt={2}>
          <Box mb={1}>
            <TextField
              color="secondary"
              disabled={saving}
              error={serverError || validator.hasError('password', submitted)}
              fullWidth
              helperText={validator.getError('password', submitted)}
              label="Enter password"
              onChange={(e: any) => {
                setPassword(e.target.value.trim());
              }}
              onKeyPress={(event: React.KeyboardEvent<HTMLDivElement>) => {
                // Submit form on enter
                if (event.key === 'Enter') submitPasswordChange();
              }}
              required
              size="small"
              type="password"
              value={password}
              variant="outlined"
            />
          </Box>
          <Box mx={1}>
            <DialogContentText
              color={serverError ? 'error' : 'textSecondary'}
              variant="caption"
            >
              {MIN_PASSWORD_LENGTH_LABEL}.
            </DialogContentText>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {closeable && (
          <Button color="inherit" disabled={saving} onClick={onClose}>
            Close
          </Button>
        )}
        <Button
          color="secondary"
          disabled={saving}
          onClick={() => submitPasswordChange()}
          variant="contained"
        >
          Set Password
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
